import React, { useEffect, useMemo } from 'react';
import { Typography, RadioGroup, Stack, Card } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider, DateCalendar } from '@mui/x-date-pickers';
import { useFormContext, useWatch } from 'react-hook-form';
import dayjs, { Dayjs } from 'dayjs';
import Grid from '@mui/material/Grid2';

import { BookingTypes, ICallProposal } from 'api';
import { useLocalization } from 'localization';
import RadioSelect from 'components/v2/radio/RadioSelect';
import { BookingFormData } from 'components/v2/division/form/schema/createOrderSchema';
import { fixBookingTimeToInterval } from 'modules/tasks/TaskBookPhotographerModal/helpers';
import TimeSelector from 'modules/v2/servicesmodal/tasks/TimeSelector';
import { createNewOrderStore } from 'stores/createNewOrderStore';
import AutoCompleteInput from 'components/v2/input/AutoCompleteInput';
import { colors } from 'theme/mui/colors';
import { IDatahubCaseDto } from 'api/cases/dtos';
import SkeletonContainer from 'components/v2/container/SkeletonContainer';
import { bookingOptions } from 'constants/options/bookingOptions';

type Props = {
  proposals: ICallProposal | undefined;
  currentCaseData?: IDatahubCaseDto;
  isGuest?: boolean;
  loading: boolean;
  disabled: boolean;
};

const BookingPhotographerModalContent = (props: Props) => {
  const { proposals, currentCaseData, loading, disabled, isGuest = false } = props;
  const { formData } = createNewOrderStore();
  const { t, f } = useLocalization();
  const {
    setValue,
    control,
    formState: { errors },
  } = useFormContext<BookingFormData>();

  const selectedDate = useWatch({ control, name: 'selectedDate' });
  const selectedTime = useWatch({ control, name: 'selectedTime' });
  const type = useWatch({ control, name: 'bookingType' });

  const secondaryAvailableDates = useMemo(
    () =>
      proposals
        ? proposals.secondary
            .filter(({ arrival }) => dayjs(arrival).isSame(selectedDate, 'day'))
            .map(item => item.arrival)
        : [],
    [proposals, selectedDate]
  );

  // to check if a date should be disabled (past dates)
  const shouldDisableDate = (current: Dayjs) => {
    if (current.isBefore(dayjs().startOf('day'))) return true;
    if (!proposals?.secondary.some(({ arrival }) => dayjs(arrival).isSame(current, 'day'))) return true;

    return false;
  };

  const currentSelectedDate = useMemo(() => {
    if (!proposals) return undefined;
    const timeSlots = proposals.secondary.find(
      ({ arrival }) =>
        dayjs(arrival).isSame(selectedDate, 'day') && dayjs(arrival).get('hours') === selectedTime?.hour()
    );

    return timeSlots;
  }, [proposals, selectedDate, selectedTime]);

  useEffect(() => {
    //we take the proposal id of the date and send it to the server
    if (currentSelectedDate) setValue('proposalId', currentSelectedDate?.id ?? '');
  }, [currentSelectedDate]);

  const transformContactPerson = useMemo(() => {
    return (
      formData?.contactPersons?.map(contact => ({
        label: contact.name ?? '',
        value: contact.phone ?? '',
      })) ??
      currentCaseData?.contactPersons?.map(contact => ({
        label: contact.name ?? '',
        value: contact.contactInfo?.phonePrivate ?? '',
      })) ??
      []
    );
  }, [currentCaseData]);

  const renderContent: Partial<Record<BookingTypes, React.JSX.Element>> = {
    [BookingTypes.BookingSecondaryConfirmed]: (
      <>
        <Typography variant="body1">{t('common.placeholders.selectTime')}</Typography>
        <RadioGroup
          value={selectedTime ?? ''}
          aria-disabled={disabled}
          onChange={(_, valueStr) => {
            setValue('selectedTime', dayjs(valueStr, 'HH:mm'));
          }}
        >
          <TimeSelector disabled={disabled} availableDates={secondaryAvailableDates} />
        </RadioGroup>
        {currentSelectedDate && (
          <Stack>
            <Stack direction="row" gap={1}>
              <Typography variant="subtitle2" fontWeight={600}>
                {`${t('modals.bookPhotographer.calendarSelectedChoise')}:`}
              </Typography>
              <Typography variant="body2" sx={{ color: colors.pink[500] }}>
                {`${f(currentSelectedDate.start, { date: 'medium' })}, ${f(currentSelectedDate.start, {
                  time: 'short',
                })} - ${f(currentSelectedDate.end, { time: 'short' })}`}
              </Typography>
            </Stack>
          </Stack>
        )}
      </>
    ),
    [BookingTypes.Booking3rdParty]: (
      <>
        <Typography variant="body2">{t('modals.bookPhotographer.contactPersonBookingSubTitle')}</Typography>
        <Typography variant="body2">{t('modals.bookPhotographer.partiallyBookingWarning')}</Typography>
        <AutoCompleteInput
          required
          control={control}
          label="common.terms.contactPerson"
          name="contactPerson"
          onChange={(_, option) => {
            setValue('contactPerson', option);
          }}
          disabled={disabled}
          options={transformContactPerson}
          otherError={errors?.contactPerson?.label}
        />
      </>
    ),
    [BookingTypes.BookingEsoft]: (
      <>
        <Typography sx={{ fontWeight: 600 }} variant="subtitle2">
          {t('modals.bookPhotographer.esoftBookingTitle')}
        </Typography>
        <Typography variant="body2">{t('orders.pages.bookOnline.esoftBookingSubtitle')}</Typography>
        <Typography variant="body2">{t('modals.bookPhotographer.partiallyBookingWarning')}</Typography>
      </>
    ),
  };

  const options = isGuest ? bookingOptions.filter(opt => opt.value !== BookingTypes.Booking3rdParty) : bookingOptions;

  return (
    <Grid container spacing={1.25} alignContent="center">
      <SkeletonContainer loading={loading} rows={{ xs: 1 }} columns={{ xs: 1 }}>
        {proposals ? (
          <Grid size={{ xs: 12 }}>
            <Stack gap={2} direction={{ xs: 'column', md: 'row' }} width="100%">
              <Stack flex={1}>
                <Card sx={{ height: { md: '100%' } }}>
                  <Typography variant="subtitle1">
                    {`${t('modals.bookPhotographer.calendarSubTitle')} 
                  ${fixBookingTimeToInterval(proposals?.primary?.timeForTask ?? proposals?.duration ?? 0)}`}
                  </Typography>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateCalendar
                      disabled={type !== BookingTypes.BookingSecondaryConfirmed || disabled}
                      value={selectedDate}
                      onChange={newDate => {
                        setValue('selectedDate', newDate);
                        setValue('selectedTime', null);
                      }}
                      shouldDisableDate={shouldDisableDate}
                      disablePast
                      sx={{
                        width: '100%',
                        '& .MuiPickersDay-root.Mui-selected': {
                          backgroundColor: colors.pink[500],
                          color: 'common.white',
                        },
                        '& .MuiPickersDay-root': {
                          minWidth: '1.5rem',
                          aspectRatio: '1 / 1',
                        },
                        '&.Mui-selected': {
                          backgroundColor: colors.pink[500],
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Card>
              </Stack>
              <Stack flex={1} gap={2}>
                <Card>
                  {renderContent[type]}
                  <Stack>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {t('common.terms.photographerComment')}:
                    </Typography>
                    <Typography variant="caption">{formData?.photographerComment ?? '-'}</Typography>
                  </Stack>
                  <Stack>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {t('modals.bookPhotographer.actions.otherOptions')}
                    </Typography>
                    <Stack>
                      <RadioSelect
                        defaultValue={options[0]}
                        options={options}
                        disabled={disabled}
                        onChange={value => {
                          setValue('bookingType', value as BookingTypes);
                          setValue('contactPerson', transformContactPerson?.[0]);
                        }}
                      />
                    </Stack>
                  </Stack>
                </Card>
              </Stack>
            </Stack>
          </Grid>
        ) : (
          <Typography>{t('modals.bookPhotographer.cannotBook')}</Typography>
        )}
      </SkeletonContainer>
    </Grid>
  );
};

export default BookingPhotographerModalContent;
